import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { disqualifyModalMode, LeadData, LeadStatus } from "@/pages/leads/types";
import { useNavigate } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import Banner from "@/components/ui/banner";
import DisqualifyLeadModal, {
  DisqualifyLeadModalState
} from "@/pages/leads/components/DisqualifyLeadModal";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import MarkAsStuckModal from "@/components/layouts/MarkAsStuckModal";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import ConvertLeadModal from "@/components/leads/ConvertLeadModal";
import { useFilteredDeals } from "@/pages/deals/hooks";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import MarkLeadAsICPToast from "@/pages/leads/components/LeadDetailedView/MarkLeadAsICPToast";
import { useAccountSettings } from "@/entities/settings/hooks";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import { resolveBooleanAccountSetting } from "@/entities/settings/accountSettingDefaults";

export const useLeadActions = (
  leadEntity: LeadData | undefined,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCopyLink = useCopyLink();

  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;
  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  const isConfirmIcpEnabled = resolveBooleanAccountSetting(
    "confirm_icp_process",
    keyValuePairs?.confirm_icp_process
  );

  const {
    lead,
    setLead,
    resetLeads,
    onUpdateLeadField: onUpdateField,
    updateLeadStatus,
    unMarkLeadAsStuck,
    deleteLead
  } = useEntityType(
    EntityType.LEAD,
    leadEntity,
    false,
    undefined,
    undefined,
    forceRefetch,
    includeAdditionals
  );

  const updateLeadState = useCallback(
    (updatedFields: Partial<LeadData>) => {
      if (lead) {
        setLead({
          ...lead,
          ...updatedFields
        });
      }
    },
    [lead, setLead]
  );

  const {
    entities: [linkedDeal]
  } = useFilteredDeals(
    { leadId: leadEntity ? leadEntity.id : "" },
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    Boolean(leadEntity)
  );

  const [isOpenDeleteLeadModal, setIsOpenDeleteLeadModal] = useState(false);
  const [isOpenMarkAsStuckModal, setIsOpenMarkAsStuckModal] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [disqualifyLeadModalState, setDisqualifyLeadModalState] =
    useState<DisqualifyLeadModalState>({
      itemToDisqualify: null,
      isOpen: false,
      mode: "disqualified",
      selectedReason: null,
      notesInput: null,
      selectedCompanyReason: null
    });
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);


  const handleDelete = (
    setIsOpenDeleteLeadModal: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (lead) {
      deleteLead.mutate(
        {
          pathParams: { leadId: lead.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpenDeleteLeadModal(false);
              closeDrawer?.();
            } else {
              navigate("/leads", { replace: true });
            }
            resetLeads();
            toast.success(
              <ToastBody title={`${t("leads.leadView.toast1")} ${lead?.name || ""} ${t("leads.leadView.toast2")}`} />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const handleStatusUpdate = (options: {
    status: LeadStatus;
    disqualifiedReason?: number;
    disqualifiedCompanyReason?: number;
    closedAsQualifiedReason?: number;
    disqualifyCompany?: boolean;
    disqualifyPerson?: boolean;
    statusNotes?: string;
    successToastContent?: string | JSX.Element;
    onSuccessFn?: () => void;
  }) => {
    if (!lead) return;
    const { status, successToastContent, onSuccessFn, ...dataFields } = options;

    updateLeadStatus.mutate(
      {
        pathParams: { leadId: lead.id },
        data: {
          status,
          ...dataFields
        }
      },
      {
        onSuccess: (updatedLead: LeadData) => {
          setLead(updatedLead);
          resetLeads();
          if (successToastContent) {
            toast.success(
              typeof successToastContent === "string" ? (
                <div>{successToastContent}</div>
              ) : (
                successToastContent
              ),
              {
                duration: 5000
              }
            );
          }
          onSuccessFn?.();
        }
      }
    );
  };

  const handleMarkLeadAsICP = () => {
    handleStatusUpdate({
      status: LeadStatus.IN_PROGRESS,
      successToastContent: <MarkLeadAsICPToast />
    });
  };

  const handleConvertLead = () => {
    handleStatusUpdate({ status: LeadStatus.CONVERTED });
  };

  const handleOpenConvertLeadModal = () => {
    setIsConvertModalOpen(true);
  };

  const handleDisqualifyLead = (data?: {
    reason: number;
    notes: string;
    disqualifyCompany: boolean;
    disqualifyPerson: boolean;
    disqualifiedCompanyReason: number;
  }) => {
    const isDisqualified = disqualifyLeadModalState.mode === "disqualified";
    handleStatusUpdate({
      status: isDisqualified
        ? LeadStatus.DISQUALIFIED
        : LeadStatus.CLOSED_QUALIFIED,
      disqualifiedReason: isDisqualified ? data?.reason : undefined,
      closedAsQualifiedReason: !isDisqualified ? data?.reason : undefined,
      disqualifyCompany: isDisqualified ? data?.disqualifyCompany : undefined,
      disqualifyPerson: isDisqualified ? data?.disqualifyPerson : undefined,
      disqualifiedCompanyReason:
        isDisqualified && data?.disqualifiedCompanyReason
          ? data?.disqualifiedCompanyReason
          : undefined,
      statusNotes: data?.notes,
      onSuccessFn: () =>
        setDisqualifyLeadModalState({
          ...disqualifyLeadModalState,
          itemToDisqualify: null,
          isOpen: false
        })
    });
  };

  const handleOpenDisqualifyLeadModal = (
    mode: disqualifyModalMode,
    selectedReason?: number | null,
    notesInput?: string | null,
    selectedCompanyReason?: number | null
  ) => {
    setDisqualifyLeadModalState({
      itemToDisqualify: lead as LeadData,
      isOpen: true,
      mode,
      selectedReason: selectedReason || null,
      notesInput: notesInput || null,
      selectedCompanyReason: selectedCompanyReason || null
    });
  };

  const handleMarkLeadAsStuck = (reason: string) => {
    handleStatusUpdate({
      status: LeadStatus.STUCK,
      statusNotes: reason,
      onSuccessFn: () => setIsOpenMarkAsStuckModal(false)
    });
  };

  const changeBackToConfirmICP = () => {
    handleStatusUpdate({ status: LeadStatus.CONFIRM_ICP });
  };

  const handleUnmarkAsStuck = () => {
    if (!lead) return;

    unMarkLeadAsStuck.mutate(
      {
        pathParams: { leadId: lead.id },
        data: {}
      },
      {
        onSuccess: (updatedLead: LeadData) => {
          setLead(updatedLead);
          resetLeads();
        }
      }
    );
  };

  const handleReopenLead = () => {
    handleStatusUpdate({
      status: LeadStatus.IN_PROGRESS,
      successToastContent: t("leads.leadView.leadInProgress"),
      onSuccessFn: () => {
        setIsOpenConfirmationModal(false);
      }
    });
  };

  const showQualifyAndConvert = () =>
    [LeadStatus.STUCK, LeadStatus.IN_PROGRESS, LeadStatus.CONFIRM_ICP].includes(
      lead?.status as LeadStatus
    );

  const getTopOptions = (isQuickView: boolean = false) => {
    const isInProgress = lead?.status === LeadStatus.IN_PROGRESS;
    const isConfirmICP = lead?.status === LeadStatus.CONFIRM_ICP;
    const isClosedQualified = lead?.status === LeadStatus.CLOSED_QUALIFIED;
    const isDisqualified = lead?.status === LeadStatus.DISQUALIFIED;
    return {
      ...(isQuickView &&
        isConfirmICP && {
          markAsIcp: handleMarkLeadAsICP,
          disqualifyLead: () => handleOpenDisqualifyLeadModal("disqualified")
        }),
      ...(isInProgress &&
        isQuickView && {
          convertLead: handleOpenConvertLeadModal,
          disqualifyLead: () => handleOpenDisqualifyLeadModal("disqualified"),
          closeLeadAsQualified: () =>
            handleOpenDisqualifyLeadModal("closedAsQualified"),
          ...(isConfirmIcpEnabled && {
            changeBackToConfirmICP: changeBackToConfirmICP
          })
        }),
      ...(isInProgress &&
        !isQuickView &&
        isConfirmIcpEnabled && {
          changeBackToConfirmICP: changeBackToConfirmICP
        }),
      ...(isDisqualified && {
        reopenLead: () => setIsOpenConfirmationModal(true),
        closeLeadAsQualified: () =>
          handleOpenDisqualifyLeadModal("closedAsQualified")
      }),
      ...(isClosedQualified && {
        reopenLead: () => setIsOpenConfirmationModal(true),
        disqualifyLead: () => handleOpenDisqualifyLeadModal("disqualified")
      })
    } as DetailedViewOptions;
  };

  const getOptions = (isQuickView: boolean = false, leadId?: string) => {
    return {
      copyLink: () => handleCopyLink(isQuickView, leadId),
      leadLog: () =>
        isQuickView
          ? navigate(`/leads/${leadId}/log`, { replace: true })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeleteLeadModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const renderQualifyMessage = () => (
    <Banner
      variant="warning"
      title={t("leads.leadView.markAsICPMessage")}
      buttonText={t("leads.leadView.markAsIcp")}
      onButtonClick={handleMarkLeadAsICP}
      showCloseButton={false}
    />
  );

  const setIsDisqualifyLeadModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    setDisqualifyLeadModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const renderModals = (
    isDropdownClosing = false,
    view: "companies" | "people" | "deals",
    setView: React.Dispatch<
      React.SetStateAction<"companies" | "people" | "deals">
    >,
    isQuickView = false,
    onClose?: () => void
  ) => (
    <>
      <DeleteEntityModal
        isOpen={isOpenDeleteLeadModal && !isDropdownClosing}
        setIsOpen={setIsOpenDeleteLeadModal}
        entity={EntityType.LEAD}
        entityName={lead?.name || ""}
        handleDelete={() =>
          handleDelete(setIsOpenDeleteLeadModal, isQuickView, onClose)
        }
      />
      <DisqualifyLeadModal
        isOpen={disqualifyLeadModalState.isOpen && !isDropdownClosing}
        setIsOpen={setIsDisqualifyLeadModalOpen}
        disqualifyLead={handleDisqualifyLead}
        mode={disqualifyLeadModalState.mode}
        selectedReason={disqualifyLeadModalState.selectedReason}
        notesInput={disqualifyLeadModalState.notesInput}
        selectedCompanyReason={disqualifyLeadModalState.selectedCompanyReason}
      />
      <MarkAsStuckModal
        isOpen={isOpenMarkAsStuckModal}
        setIsOpen={setIsOpenMarkAsStuckModal}
        handleSubmit={handleMarkLeadAsStuck}
        tKey="leads.leadView"
      />
      <ConfirmationModal
        isOpen={isOpenConfirmationModal && !isDropdownClosing}
        setIsOpen={setIsOpenConfirmationModal}
        title={t("leads.leadView.reopenLead")}
        confirmationText={t("leads.leadView.reopenLeadConfirmation")}
        handleSubmit={handleReopenLead}
        isPrimaryButton
        submitButtonText={t("confirm")}
      />
      <ConvertLeadModal
        isOpen={isConvertModalOpen}
        setIsOpen={setIsConvertModalOpen}
        lead={lead as LeadData}
        handleConvertLead={handleConvertLead}
      />
      <CreatePersonModal
        isOpen={view === "people"}
        setIsOpen={(open) => setView(open ? "people" : "deals")}
        companyId={lead?.companyId}
        onSuccess={(newPersonId: string) => {
          onUpdateField("contactId", newPersonId);
        }}
      />
    </>
  );

  return {
    lead,
    updateLeadState,
    linkedDeal,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    handleMarkLeadAsICP,
    handleOpenConvertLeadModal,
    handleConvertLead,
    handleOpenDisqualifyLeadModal,
    handleUnmarkAsStuck,
    onUpdateField,
    getTopOptions,
    getOptions,
    renderQualifyMessage,
    renderModals,
    showQualifyAndConvert
  };
};
